import Notification from './notification.service';

const commonDefaults = {
  error: `Something went wrong`,
  success: `Success`,
  400: `Some required fields are missing`,
  401: `You don't have the permissions required for this action`,
  404: `The requested item was not found`,
  422: `Some of the data was invalid`,
};

/**
 * Evaluates the string message for display in a notification.
 *
 * @param {String|Function|Object} messageField
 *  If String, it is returned as is.
 *  If Function, messageField(functionArgument) is returned.
 *  If Object
 *    The value of messageField.skipEvaluationInWrapper is checked. If truthy, '' is returned.
 *    Otherwise, the function is recursively called giving messageField.message as the first argument.
 * @param {any} functionArgument Passed in as the function argument to a function call of messageField, when messageField is a function
 * @return {String} The evaluated notification text
 */
function evaluateNotificationText(messageField, functionArgument) {
  if (_.isFunction(messageField)) {
    return messageField(functionArgument);
  }
  if (_.isString(messageField)) {
    return messageField;
  }

  // Expect it to be an object
  if (messageField.skipEvaluationInWrapper) {
    return '';
  }
  return messageField.message ? evaluateNotificationText(messageField.message, functionArgument) : '';
}

/**
 * Displays a notification in the UI. The notification text is calculated using the evaluateNotificationText function, passing it the messageField and functionArgument.
 * If messageField is given as an object, messageField.customNotificationConfig can be used to override the default config for the calculated notification type.
 *
 * @param {String|Function|Object} messageField used to evaluate the notification text, and to override notification type and config
 * @param {any} functionArgument used to evaluate the notification text when messageField is of type function
 * @param {Boolean} isError determines the type of notification, unless messageField is an object and messageField.notificationType is given
 */
function showNotification(messageField, functionArgument, isError) {
  let notificationText = evaluateNotificationText(messageField, functionArgument);
  let notificationType = isError ? 'error' : 'info';
  let customNotificationConfig = {};

  if (notificationText && !_.isFunction(messageField) && !_.isString(messageField)) {
    // Expect it to be an object
    notificationType = messageField.notificationType || notificationType;
    customNotificationConfig = messageField.customNotificationConfig;
  }

  if (notificationType === 'error') {
    const statusCode = _.get(functionArgument, 'response.status');
    notificationText = statusCode && notificationText ? `${notificationText} (${statusCode})` : notificationText;
  }

  Notification.show(notificationText, notificationType, customNotificationConfig);
}

export function withNotifications(functionToWrap, messages) {
  return async (...args) => {
    try {
      const result = await functionToWrap(...args);
      const successMessage = [
        messages.success,
        commonDefaults.success,
      ].find(message => message !== undefined);
      showNotification(successMessage, result);

      return result;
    } catch (error) {
      const statusCode = _.get(error, 'response.status');
      const errorMessage = [
        messages[statusCode],
        messages.error,
        commonDefaults[statusCode],
        commonDefaults.error,
      ].find(message => message !== undefined);
      showNotification(errorMessage, error, true);

      throw error;
    }
  };
}

export function functionsWithNotifications(functions, mappedMessages) {
  const wrapped = _.clone(functions);
  Object.keys(wrapped).forEach((key) => {
    const messages = {
      ...(mappedMessages.default || {}),
      ...(mappedMessages[key] || {}),
    };
    wrapped[key] = withNotifications(wrapped[key], messages);
  });
  return wrapped;
}
